/**
 * The catalogue admin screen shows categories, subcategories and the modules
 * inside them as a single hierarchy, so this module owns the two things that
 * view needs: turning the flat folder and item lists into ordered rows, and
 * deciding what a drag actually changes.
 *
 * Both are kept away from React because the drag rules — no folder inside its
 * own subtree, siblings renumbered so the order survives a reload — are worth
 * testing directly.
 */

/** The folder fields the tree reads; the domain `CatalogFolder` satisfies it. */
export interface CatalogTreeFolder {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
}

/** The module fields the tree reads; the domain `CatalogItem` satisfies it. */
export interface CatalogTreeItem {
  id: string;
  name: string;
  folderId?: string | null;
  /** Absent on modules imported before the order was stored; they fall back to the name. */
  sortOrder?: number;
}

export interface CatalogFolderRow<F> {
  kind: "folder";
  key: string;
  depth: number;
  folder: F;
  /** Modules bound to this folder alone. */
  directCount: number;
  /** Modules in this folder and every folder below it. */
  totalCount: number;
  hasChildren: boolean;
  expanded: boolean;
}

export interface CatalogItemRow<I> {
  kind: "item";
  key: string;
  depth: number;
  item: I;
  /** Null for modules whose folder was deleted or never set. */
  folderId: string | null;
}

/** Heading for the modules that answer to no folder. */
export interface CatalogUnfiledRow {
  kind: "unfiled";
  key: string;
  depth: 0;
  count: number;
  expanded: boolean;
}

export type CatalogRow<F, I> = CatalogFolderRow<F> | CatalogItemRow<I> | CatalogUnfiledRow;

export interface CatalogFolderCounts {
  direct: ReadonlyMap<string, number>;
  total: ReadonlyMap<string, number>;
}

/** Identifier of the pseudo-folder that groups modules without a folder. */
export const UNFILED_FOLDER_KEY = "__unfiled__";

/**
 * Children of each folder, ordered the way the user arranged them. `sortOrder`
 * decides; the name only breaks ties, which happens when two folders were
 * created before anyone dragged anything.
 */
function groupChildren<F extends CatalogTreeFolder>(folders: readonly F[]): Map<string | null, F[]> {
  const children = new Map<string | null, F[]>();
  for (const folder of folders) {
    const parentId = folder.parentId ?? null;
    const siblings = children.get(parentId) ?? [];
    siblings.push(folder);
    children.set(parentId, siblings);
  }
  for (const siblings of children.values()) {
    siblings.sort((left, right) =>
      left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, "pt-BR", { sensitivity: "base" }));
  }
  return children;
}

/**
 * As categorias de um nível, na ordem em que o cadastro as arrumou.
 *
 * O editor mostra as mesmas categorias que a administração, e mostrar em outra
 * ordem é o mesmo que mostrar outra coisa: quem arrastou "Heitor" para o segundo
 * lugar espera achá-lo no segundo lugar ao abrir um projeto. Por isso a conta é
 * uma só, e mora aqui — em ordem alfabética ela ficava no fim da fila.
 */
export function orderedChildFolders<F extends CatalogTreeFolder>(
  folders: readonly F[],
  parentId: string | null = null,
): F[] {
  return groupChildren(folders).get(parentId) ?? [];
}

export function isCatalogFolderAtOrBelow<F extends CatalogTreeFolder>(
  folders: readonly F[],
  folderId: string,
  ancestorId: string,
): boolean {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const visited = new Set<string>();
  let currentId: string | null = folderId;
  while (currentId && !visited.has(currentId)) {
    if (currentId === ancestorId) return true;
    visited.add(currentId);
    currentId = byId.get(currentId)?.parentId ?? null;
  }
  return false;
}

/**
 * The order the modules of one folder are shown in. `sortOrder` is what the
 * user arranged; the name only decides between modules that never got one.
 */
function compareItems(left: CatalogTreeItem, right: CatalogTreeItem): number {
  return (left.sortOrder ?? 0) - (right.sortOrder ?? 0)
    || left.name.localeCompare(right.name, "pt-BR", { sensitivity: "base" });
}

export type CatalogShelfEntry<F, I> =
  | { kind: "folder"; folder: F }
  | { kind: "item"; item: I };

/**
 * One catalogue shelf: child folders first, then only the modules filed
 * directly at this level. Descendants belong to the folder card that opens
 * them instead of leaking into a second row of category tabs.
 */
export function buildCatalogShelf<F extends CatalogTreeFolder, I extends CatalogTreeItem>(input: {
  folders: readonly F[];
  items: readonly I[];
  parentId: string;
}): Array<CatalogShelfEntry<F, I>> {
  const folders = orderedChildFolders(input.folders, input.parentId);
  const items = input.items
    .filter((item) => item.folderId === input.parentId)
    .sort(compareItems);
  return [
    ...folders.map((folder) => ({ kind: "folder" as const, folder })),
    ...items.map((item) => ({ kind: "item" as const, item })),
  ];
}

/**
 * A folder plus everything under it. Used to block a drag that would put a
 * folder inside itself and to count the modules of a whole branch.
 */
export function collectFolderSubtree(
  folders: readonly CatalogTreeFolder[],
  folderId: string,
): Set<string> {
  const childIds = new Map<string, string[]>();
  for (const folder of folders) {
    if (!folder.parentId) continue;
    const siblings = childIds.get(folder.parentId) ?? [];
    siblings.push(folder.id);
    childIds.set(folder.parentId, siblings);
  }
  const subtree = new Set<string>();
  const walk = (id: string): void => {
    if (subtree.has(id)) return;
    subtree.add(id);
    for (const childId of childIds.get(id) ?? []) walk(childId);
  };
  walk(folderId);
  return subtree;
}

export function countCatalogFolders(
  folders: readonly CatalogTreeFolder[],
  items: readonly CatalogTreeItem[],
): CatalogFolderCounts {
  const direct = new Map<string, number>();
  for (const folder of folders) direct.set(folder.id, 0);
  for (const item of items) {
    const folderId = item.folderId ?? null;
    if (folderId === null || !direct.has(folderId)) continue;
    direct.set(folderId, (direct.get(folderId) ?? 0) + 1);
  }
  const total = new Map<string, number>();
  for (const folder of folders) {
    let sum = 0;
    for (const id of collectFolderSubtree(folders, folder.id)) sum += direct.get(id) ?? 0;
    total.set(folder.id, sum);
  }
  return { direct, total };
}

/**
 * The rows in display order: each folder followed by its subfolders and its
 * modules, then the modules nobody filed. A collapsed folder hides its branch
 * but still reports what is inside it.
 */
export function buildCatalogRows<F extends CatalogTreeFolder, I extends CatalogTreeItem>(input: {
  folders: readonly F[];
  items: readonly I[];
  openFolders: ReadonlySet<string>;
  counts?: CatalogFolderCounts;
}): Array<CatalogRow<F, I>> {
  const counts = input.counts ?? countCatalogFolders(input.folders, input.items);
  const children = groupChildren(input.folders);
  const known = new Set(input.folders.map((folder) => folder.id));
  const itemsByFolder = new Map<string, I[]>();
  const unfiled: I[] = [];
  for (const item of input.items) {
    const folderId = item.folderId ?? null;
    if (folderId === null || !known.has(folderId)) {
      unfiled.push(item);
      continue;
    }
    const bucket = itemsByFolder.get(folderId) ?? [];
    bucket.push(item);
    itemsByFolder.set(folderId, bucket);
  }
  for (const bucket of itemsByFolder.values()) bucket.sort(compareItems);
  unfiled.sort(compareItems);

  const rows: Array<CatalogRow<F, I>> = [];
  const emit = (folder: F, depth: number): void => {
    const subfolders = children.get(folder.id) ?? [];
    const modules = itemsByFolder.get(folder.id) ?? [];
    const expanded = input.openFolders.has(folder.id);
    rows.push({
      kind: "folder",
      key: `folder:${folder.id}`,
      depth,
      folder,
      directCount: counts.direct.get(folder.id) ?? 0,
      totalCount: counts.total.get(folder.id) ?? 0,
      hasChildren: subfolders.length > 0 || modules.length > 0,
      expanded,
    });
    if (!expanded) return;
    for (const child of subfolders) emit(child, depth + 1);
    for (const item of modules) {
      rows.push({ kind: "item", key: `item:${item.id}`, depth: depth + 1, item, folderId: folder.id });
    }
  };
  for (const folder of children.get(null) ?? []) emit(folder, 0);

  if (unfiled.length) {
    const expanded = input.openFolders.has(UNFILED_FOLDER_KEY);
    rows.push({ kind: "unfiled", key: "unfiled", depth: 0, count: unfiled.length, expanded });
    if (expanded) {
      for (const item of unfiled) {
        rows.push({ kind: "item", key: `item:${item.id}`, depth: 1, item, folderId: null });
      }
    }
  }
  return rows;
}

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
}

export interface CatalogFilterResult<F, I> {
  folders: F[];
  items: I[];
  /** Folders that must stay open so the matches are visible. */
  openFolders: Set<string>;
}

/**
 * Narrows the tree to what a search touches. A folder survives when its own
 * name matches, when something below it matches, or when it is on the way to a
 * matching module — and a matching folder brings its whole branch with it, so
 * searching for a category shows the modules it holds.
 */
export function filterCatalogTree<F extends CatalogTreeFolder, I extends CatalogTreeItem>(input: {
  folders: readonly F[];
  items: readonly I[];
  search: string;
  itemText?: (item: I) => string;
}): CatalogFilterResult<F, I> {
  const query = normalize(input.search.trim());
  if (!query) {
    return { folders: [...input.folders], items: [...input.items], openFolders: new Set() };
  }
  const itemText = input.itemText ?? ((item: I) => item.name);
  const parentById = new Map(input.folders.map((folder) => [folder.id, folder.parentId ?? null]));

  const matchedFolders = new Set(
    input.folders.filter((folder) => normalize(folder.name).includes(query)).map((folder) => folder.id),
  );
  const insideMatchedFolder = new Set<string>();
  for (const id of matchedFolders) {
    for (const descendant of collectFolderSubtree(input.folders, id)) insideMatchedFolder.add(descendant);
  }

  const keptFolders = new Set<string>();
  const keep = (folderId: string | null): void => {
    let current = folderId;
    while (current && !keptFolders.has(current)) {
      keptFolders.add(current);
      current = parentById.get(current) ?? null;
    }
  };
  for (const id of insideMatchedFolder) keep(id);

  const items = input.items.filter((item) => {
    const folderId = item.folderId ?? null;
    if (folderId && insideMatchedFolder.has(folderId)) return true;
    if (!normalize(itemText(item)).includes(query)) return false;
    keep(folderId);
    return true;
  });

  return {
    folders: input.folders.filter((folder) => keptFolders.has(folder.id)),
    items,
    openFolders: new Set([...keptFolders, UNFILED_FOLDER_KEY]),
  };
}

export type CatalogDropPlacement = "before" | "after" | "inside";

export interface CatalogFolderPatch {
  id: string;
  parentId: string | null;
  sortOrder: number;
}

/** The step between siblings after a drop; leaves room for the server to sort. */
const SORT_STEP = 100;

/** Where a new folder goes when created from a row: after the last sibling. */
export function nextFolderSortOrder(
  folders: readonly CatalogTreeFolder[],
  parentId: string | null,
): number {
  const siblings = folders.filter((folder) => (folder.parentId ?? null) === parentId);
  if (!siblings.length) return SORT_STEP;
  return Math.max(...siblings.map((folder) => folder.sortOrder)) + SORT_STEP;
}

/**
 * What a folder drag changes. `targetId` null is the empty space below the
 * tree, which means "make it a top-level category, last".
 *
 * The whole destination row is renumbered rather than squeezed into the gap
 * between two neighbours: the gap can be zero after enough drags, and a
 * catalogue has few folders, so rewriting them is cheaper than the arithmetic
 * to avoid it. Only the folders that really moved come back.
 */
export function resolveFolderDrop(
  folders: readonly CatalogTreeFolder[],
  draggedId: string,
  targetId: string | null,
  placement: CatalogDropPlacement,
): CatalogFolderPatch[] {
  const dragged = folders.find((folder) => folder.id === draggedId);
  if (!dragged) return [];
  if (targetId === draggedId) return [];
  const target = targetId ? folders.find((folder) => folder.id === targetId) ?? null : null;
  if (targetId && !target) return [];
  if (targetId && collectFolderSubtree(folders, draggedId).has(targetId)) return [];

  const parentId = target === null ? null : placement === "inside" ? target.id : target.parentId ?? null;
  const children = groupChildren(folders);
  const siblings = (children.get(parentId) ?? []).filter((folder) => folder.id !== draggedId);
  let index = siblings.length;
  if (target && placement !== "inside") {
    const position = siblings.findIndex((folder) => folder.id === target.id);
    index = position < 0 ? siblings.length : position + (placement === "after" ? 1 : 0);
  }
  const ordered = [...siblings.slice(0, index), dragged, ...siblings.slice(index)];

  const patches: CatalogFolderPatch[] = [];
  ordered.forEach((folder, position) => {
    const sortOrder = (position + 1) * SORT_STEP;
    if ((folder.parentId ?? null) === parentId && folder.sortOrder === sortOrder) return;
    patches.push({ id: folder.id, parentId, sortOrder });
  });
  return patches;
}

export interface CatalogItemPatch {
  id: string;
  folderId: string;
  sortOrder: number;
}

export interface CatalogItemDrop {
  items: readonly CatalogTreeItem[];
  draggedId: string;
  /** The folder the module lands in; a drop that would unfile it is refused. */
  folderId: string | null;
  /** Set when the drop landed on another module rather than on the folder itself. */
  targetItemId?: string | null;
  /** Only read alongside `targetItemId`; "inside" reads as "after". */
  placement?: CatalogDropPlacement;
}

/**
 * Moving a module, and now also placing it: dropped on a folder it goes to the
 * end, dropped on the edge of another module it takes that module's place.
 *
 * Like the folders, the destination folder is renumbered and only the modules
 * whose position really changed come back — one drag is usually one write.
 */
export function resolveItemDrop(input: CatalogItemDrop): CatalogItemPatch[] {
  const { folderId, draggedId } = input;
  if (!folderId) return [];
  const dragged = input.items.find((item) => item.id === draggedId);
  if (!dragged) return [];
  if (input.targetItemId === draggedId) return [];

  const siblings = input.items
    .filter((item) => (item.folderId ?? null) === folderId && item.id !== draggedId)
    .sort(compareItems);
  let index = siblings.length;
  if (input.targetItemId) {
    const position = siblings.findIndex((item) => item.id === input.targetItemId);
    if (position >= 0) index = position + (input.placement === "before" ? 0 : 1);
  }
  const ordered = [...siblings.slice(0, index), dragged, ...siblings.slice(index)];

  const patches: CatalogItemPatch[] = [];
  ordered.forEach((item, position) => {
    const sortOrder = (position + 1) * SORT_STEP;
    if ((item.folderId ?? null) === folderId && item.sortOrder === sortOrder) return;
    patches.push({ id: item.id, folderId, sortOrder });
  });
  return patches;
}
